import { useEffect } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface SmoothScrollProviderProps {
  children: React.ReactNode;
}

export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
  useEffect(() => {
    // Initialize Lenis
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 2,
    });

    // Integrate with existing GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Keep ScrollTrigger pin measurements in sync with LATE layout changes.
    // Pin start/end are computed from the layout at refresh time, but several
    // things change section heights AFTER that: web fonts reflowing text, the
    // Spline / Three.js scenes settling, and the width-conditional re-renders
    // (`isLargeScreen` in About Me + SkillGrid, `isDesktop` in Case Studies).
    // None of those fire a window 'resize', so without this the pinned sections
    // (How I Work / Case Studies / About Me) stay measured at stale offsets and
    // overlap their neighbours. A debounced refresh on any body size change
    // (ResizeObserver) plus on font load covers all of them.
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleRefresh);
    }

    const resizeObserver = new ResizeObserver(scheduleRefresh);
    resizeObserver.observe(document.body);

    // Cleanup
    return () => {
      clearTimeout(refreshTimer);
      resizeObserver.disconnect();
      lenis.destroy();
      gsap.ticker.remove((time) => lenis.raf(time * 1000));
    };
  }, []);

  return <>{children}</>;
}